'use client';

import React, { memo } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import {
  Download,
  Trash2,
  Link2,
  Share2,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { Button } from '@/shared/ui/shadcn/button';
import { Progress } from '@/shared/ui/shadcn/progress';
import { AlbumCoverView } from '@/features/design/history/ui/AlbumCoverView';
import { getLocaleByCode } from '@/shared/constants';
import { formatDateTime } from '@/shared/lib/utils';
import type { Album } from '../api/album-service';

interface AlbumListRowProps {
  album: Album;
  conceptTitle: string | null;
  conceptNumber: number;
  coverImageUrl: string | null;
  onDelete?: () => void;
  onDownload?: () => void;
  onCopyLink?: () => void;
  onShare?: () => void;
  /** Tile click (whole row except action buttons) — e.g. navigate to the concept's Album tab. */
  onTap?: () => void;
}

/**
 * Album list row with cover thumbnail and metadata.
 * Matches Android/Apple AlbumListRow — vertical on mobile, horizontal on desktop.
 */
export const AlbumListRow = memo(function AlbumListRow({
  album,
  conceptTitle,
  conceptNumber,
  coverImageUrl,
  onDelete,
  onDownload,
  onCopyLink,
  onShare,
  onTap,
}: AlbumListRowProps) {
  const t = useTranslations('album');
  const tRender = useTranslations('render.status');
  const locale = useLocale();

  const isCompleted = album.status === 'completed';
  const isFailed = album.status === 'failed';
  const isProcessing =
    album.status === 'pending' || album.status === 'processing';

  // Label reflects whether blueprints were bundled (`include_blueprints`) \u2014
  // the design template is always `standard` now, so it carries no signal here.
  const blueprintLabel = album.include_blueprints
    ? t('blueprints.withLabel')
    : t('blueprints.withoutLabel');
  const formatText = `${blueprintLabel} \u2022 ${album.format}`;
  // Apple parity: show the locale's own endonym ("Русский", "Español") rather
  // than the EN exonym ("Russian", "Spanish") — matches AlbumCoverView in the
  // Apple app.
  const languageName =
    getLocaleByCode(album.language)?.nativeName || album.language.toUpperCase();

  const dateStr = formatDateTime(
    isCompleted && album.generated_at ? album.generated_at : album.created_at,
    locale
  );

  return (
    <div className="@container">
      {/* Vertical (full-width cover + captions below) until ~800px, then
          horizontal list. Calibrated so the tablet concept-detail right pane
          with the app sidebar OPEN renders vertically and with the sidebar
          collapsed becomes the horizontal list — matches Apple/Android. */}
      <div
        className={`flex flex-col @[800px]:flex-row ${onTap ? 'cursor-pointer' : ''}`}
        onClick={onTap}
      >
        {/* Cover */}
        <div className="@[800px]:w-[200px] @[800px]:flex-shrink-0">
          <div className="aspect-[4/3]">
            <AlbumCoverView
              coverImageUrl={coverImageUrl}
              conceptTitle={conceptTitle}
              conceptNumber={conceptNumber}
              format={album.format}
              createdAt={album.created_at}
              status={album.status}
              albumTitle={album.album_title}
            />
          </div>
        </div>

        {/* Metadata + actions */}
        <div className="flex flex-1 items-center justify-between py-3 @[800px]:p-4 min-w-0">
          <div className="flex-1 min-w-0">
            {/* Format + language */}
            <div className="flex items-center gap-1.5 text-card-meta text-muted-foreground">
              <span>{formatText}</span>
              <span className="opacity-50">&bull;</span>
              <span>{languageName}</span>
            </div>

            {/* Date */}
            <div className="text-card-meta text-muted-foreground/70 mt-0.5">
              {dateStr}
            </div>

            {/* Status badge (left) + action buttons (right) — same row in vertical layout */}
            <div className="flex items-center justify-between mt-1.5">
              <div>
                {isCompleted && (album.file_size || album.page_count) && (
                  <div className="flex items-center gap-1 text-card-meta text-muted-foreground">
                    {album.file_size && <span>{album.file_size}</span>}
                    {album.file_size && album.page_count && (
                      <span className="opacity-60">•</span>
                    )}
                    {album.page_count && (
                      <span>{t('pageCountFormat', { count: album.page_count })}</span>
                    )}
                  </div>
                )}

                {isFailed && (
                  <div className="flex items-center gap-1 text-card-meta text-destructive">
                    <AlertCircle className="h-3 w-3" />
                    <span>{t('failed')}</span>
                  </div>
                )}

                {isProcessing && (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-3 w-3 animate-spin text-primary" />
                      <span className="text-progress-meta text-muted-foreground">
                        {tRender('generatingProgress', {
                          progress: album.status === 'pending' ? 0 : 50,
                        })}
                      </span>
                    </div>
                    <Progress
                      value={album.status === 'pending' ? 0 : 50}
                      className="h-1"
                    />
                  </div>
                )}
              </div>

              {/* Action buttons — shown in vertical (narrow) layout only.
                  stopPropagation keeps icon clicks out of the row's onTap. */}
              <div className="flex @[800px]:hidden" onClick={(e) => e.stopPropagation()}>
                {isCompleted && album.public_url && onCopyLink && (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-9 w-9"
                    onClick={onCopyLink}
                  >
                    <Link2 className="h-4 w-4" />
                  </Button>
                )}
                {isCompleted && album.public_url && onShare && (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-9 w-9"
                    onClick={onShare}
                  >
                    <Share2 className="h-4 w-4" />
                  </Button>
                )}
                {(isCompleted || isFailed) && onDelete && (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-9 w-9"
                    onClick={onDelete}
                  >
                    <Trash2 className="h-4 w-4 text-muted-foreground/60" />
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Action buttons — horizontal layout only.
              stopPropagation keeps icon clicks out of the row's onTap. */}
          <div
            className="hidden @[800px]:flex items-center gap-1 ml-4"
            onClick={(e) => e.stopPropagation()}
          >
            {(isCompleted || isFailed) && onDelete && (
              <Button size="sm" variant="ghost" onClick={onDelete}>
                <Trash2 className="h-4 w-4 text-muted-foreground/60" />
              </Button>
            )}
            {isCompleted && album.public_url && (
              <>
                {onDownload && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="border border-border/30 hover:border-border/50"
                    onClick={onDownload}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                )}
                {onCopyLink && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="border border-border/30 hover:border-border/50"
                    onClick={onCopyLink}
                  >
                    <Link2 className="h-4 w-4" />
                  </Button>
                )}
                {onShare && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="border border-border/30 hover:border-border/50"
                    onClick={onShare}
                  >
                    <Share2 className="h-4 w-4" />
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});
